import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  Users,
  Server,
  Database,
  LogOut,
  RefreshCw,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Monitor,
  Cpu,
  HardDrive,
} from 'lucide-react';

interface AdminConsoleProps {
  onLogout: () => void;
}

interface SystemMetrics {
  cpu: number;
  memory: number;
  disk: number;
  network: number;
  uptime: number;
}

interface UserSession {
  id: string;
  ip: string;
  userAgent: string;
  loginTime: string;
  lastActivity: string;
  isActive: boolean;
  sessionType: 'admin' | 'user';
}

interface ApiMetrics {
  totalRequests: number;
  averageResponseTime: number;
  errorRate: number;
  cacheHitRate: number;
}

const AdminConsole: React.FC<AdminConsoleProps> = ({ onLogout }) => {
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics>({
    cpu: 0,
    memory: 0,
    disk: 0,
    network: 0,
    uptime: 0,
  });
  const [userSessions, setUserSessions] = useState<UserSession[]>([]);
  const [apiMetrics, setApiMetrics] = useState<ApiMetrics>({
    totalRequests: 0,
    averageResponseTime: 0,
    errorRate: 0,
    cacheHitRate: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [isRestarting, setIsRestarting] = useState(false);

  // Admin API base URL
  const ADMIN_API_BASE =
    process.env.NODE_ENV === 'production'
      ? `${window.location.protocol}//${window.location.host}`
      : 'http://localhost:3000'; // Use the React dev server proxy

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      // Use the correct endpoint URLs that match the server implementation
      const [systemResponse, sessionsResponse, apiResponse] = await Promise.all(
        [
          fetch(`${ADMIN_API_BASE}/api/v1/admin/system-metrics`),
          fetch(`${ADMIN_API_BASE}/api/v1/admin/user-sessions`),
          fetch(`${ADMIN_API_BASE}/api/v1/admin/api-metrics`),
        ]
      );

      if (!systemResponse.ok || !sessionsResponse.ok || !apiResponse.ok) {
        throw new Error('Failed to fetch admin data');
      }

      const systemData = await systemResponse.json();
      const sessionsData = await sessionsResponse.json();
      const apiData = await apiResponse.json();

      setSystemMetrics(systemData);
      setUserSessions(sessionsData);
      setApiMetrics(apiData);
    } catch (err: any) {
      console.error('Error fetching admin data:', err);
      setError(
        err.message ||
          'Failed to load admin data. Please check server connection.'
      );

      // Reset to empty/zero states instead of dummy data
      setSystemMetrics({
        cpu: 0,
        memory: 0,
        disk: 0,
        network: 0,
        uptime: 0,
      });
      setUserSessions([]);
      setApiMetrics({
        totalRequests: 0,
        averageResponseTime: 0,
        errorRate: 0,
        cacheHitRate: 0,
      });
    } finally {
      setIsLoading(false);
      setLastRefresh(new Date());
    }
  }, [ADMIN_API_BASE]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setIsLoading(true);
    fetchData();
  };

  const handleClearRAM = async () => {
    try {
      const adminSessionId = localStorage.getItem('adminSessionId');
      const response = await fetch(
        `${ADMIN_API_BASE}/api/v1/admin/system/clear-ram`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminSessionId}`,
          },
        }
      );

      if (response.ok) {
        // Refresh data after clearing RAM
        handleRefresh();
      } else {
        setError('Failed to clear RAM');
      }
    } catch (error) {
      setError('Failed to clear RAM');
    }
  };

  const handleRestartServer = async () => {
    if (!window.confirm('Are you sure you want to restart the server?')) {
      return;
    }

    try {
      setIsRestarting(true);
      const adminSessionId = localStorage.getItem('adminSessionId');
      await fetch(`${ADMIN_API_BASE}/api/v1/admin/system/restart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminSessionId}`,
        },
      });

      // Server will restart, so we logout and redirect
      setTimeout(() => {
        onLogout();
        window.location.reload();
      }, 2000);
    } catch (error) {
      setError('Failed to restart server');
      setIsRestarting(false);
    }
  };

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const getStatusColor = (value: number, type: 'cpu' | 'memory' | 'disk') => {
    const thresholds = {
      cpu: { warning: 70, critical: 90 },
      memory: { warning: 80, critical: 95 },
      disk: { warning: 80, critical: 95 },
    };

    const threshold = thresholds[type];
    if (value >= threshold.critical) return 'text-red-500';
    if (value >= threshold.warning) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getStatusIcon = (value: number, type: 'cpu' | 'memory' | 'disk') => {
    const thresholds = {
      cpu: { warning: 70, critical: 90 },
      memory: { warning: 80, critical: 95 },
      disk: { warning: 80, critical: 95 },
    };

    const threshold = thresholds[type];
    if (value >= threshold.critical)
      return <XCircle className='w-5 h-5 text-red-500' />;
    if (value >= threshold.warning)
      return <AlertTriangle className='w-5 h-5 text-yellow-500' />;
    return <CheckCircle className='w-5 h-5 text-green-500' />;
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-6'>
      <div className='max-w-7xl mx-auto'>
        {/* Header */}
        <div className='bg-white rounded-2xl shadow-2xl p-6 mb-6'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-4'>
              <div className='w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center'>
                <Monitor className='text-white' size={24} />
              </div>
              <div>
                <h1 className='text-2xl font-bold text-gray-900'>
                  Admin Console
                </h1>
                <p className='text-gray-600'>
                  System monitoring and management
                </p>
              </div>
            </div>
            <div className='flex items-center gap-3'>
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className='flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all'
              >
                <RefreshCw
                  className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
                />
                Refresh
              </button>
              <button
                onClick={onLogout}
                className='flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all'
              >
                <LogOut className='w-4 h-4' />
                Logout
              </button>
            </div>
          </div>
          {lastRefresh && (
            <p className='text-sm text-gray-500 mt-2'>
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          )}
        </div>

        {error && (
          <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
            <div className='flex items-center gap-2 text-red-700'>
              <AlertTriangle size={16} />
              {error}
            </div>
          </div>
        )}

        {/* System Metrics Grid */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6'>
          <div className='bg-white rounded-xl shadow-lg p-6'>
            <div className='flex items-center justify-between mb-4'>
              <div className='flex items-center gap-3'>
                <Cpu className='text-blue-600' size={24} />
                <h3 className='font-semibold text-gray-900'>CPU Usage</h3>
              </div>
              {getStatusIcon(systemMetrics.cpu, 'cpu')}
            </div>
            <div className='space-y-2'>
              <div
                className={`text-2xl font-bold ${getStatusColor(systemMetrics.cpu, 'cpu')}`}
              >
                {systemMetrics.cpu.toFixed(1)}%
              </div>
              <div className='w-full bg-gray-200 rounded-full h-2'>
                <div
                  className='bg-blue-600 h-2 rounded-full transition-all'
                  style={{ width: `${Math.min(systemMetrics.cpu, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className='bg-white rounded-xl shadow-lg p-6'>
            <div className='flex items-center justify-between mb-4'>
              <div className='flex items-center gap-3'>
                <Activity className='text-green-600' size={24} />
                <h3 className='font-semibold text-gray-900'>Memory</h3>
              </div>
              {getStatusIcon(systemMetrics.memory, 'memory')}
            </div>
            <div className='space-y-2'>
              <div
                className={`text-2xl font-bold ${getStatusColor(systemMetrics.memory, 'memory')}`}
              >
                {systemMetrics.memory.toFixed(1)}%
              </div>
              <div className='w-full bg-gray-200 rounded-full h-2'>
                <div
                  className='bg-green-600 h-2 rounded-full transition-all'
                  style={{ width: `${Math.min(systemMetrics.memory, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className='bg-white rounded-xl shadow-lg p-6'>
            <div className='flex items-center justify-between mb-4'>
              <div className='flex items-center gap-3'>
                <HardDrive className='text-purple-600' size={24} />
                <h3 className='font-semibold text-gray-900'>Disk Usage</h3>
              </div>
              {getStatusIcon(systemMetrics.disk, 'disk')}
            </div>
            <div className='space-y-2'>
              <div
                className={`text-2xl font-bold ${getStatusColor(systemMetrics.disk, 'disk')}`}
              >
                {systemMetrics.disk.toFixed(1)}%
              </div>
              <div className='w-full bg-gray-200 rounded-full h-2'>
                <div
                  className='bg-purple-600 h-2 rounded-full transition-all'
                  style={{ width: `${Math.min(systemMetrics.disk, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className='bg-white rounded-xl shadow-lg p-6'>
            <div className='flex items-center gap-3 mb-4'>
              <Server className='text-orange-600' size={24} />
              <h3 className='font-semibold text-gray-900'>Uptime</h3>
            </div>
            <div className='text-2xl font-bold text-gray-900'>
              {formatUptime(systemMetrics.uptime)}
            </div>
            <div className='text-sm text-gray-500 mt-1'>
              Network: {systemMetrics.network.toFixed(1)} MB/s
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='bg-white rounded-xl shadow-lg p-6 mb-6'>
          <h3 className='font-semibold text-gray-900 mb-4'>System Controls</h3>
          <div className='flex gap-4'>
            <button
              onClick={handleClearRAM}
              className='flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all'
            >
              <Trash2 className='w-4 h-4' />
              Clear RAM Cache
            </button>
            <button
              onClick={handleRestartServer}
              disabled={isRestarting}
              className='flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-all'
            >
              <Server className='w-4 h-4' />
              {isRestarting ? 'Restarting...' : 'Restart Server'}
            </button>
          </div>
        </div>

        {/* User Sessions */}
        <div className='bg-white rounded-xl shadow-lg p-6 mb-6'>
          <div className='flex items-center gap-3 mb-4'>
            <Users className='text-blue-600' size={24} />
            <h3 className='font-semibold text-gray-900'>
              Active User Sessions ({userSessions.length})
            </h3>
          </div>
          {userSessions.length === 0 ? (
            <div className='text-center py-8 text-gray-500'>
              <Users size={48} className='mx-auto mb-4 opacity-30' />
              <p className='text-lg font-medium'>No Active Sessions</p>
              <p className='text-sm'>
                No user sessions are currently active on the system.
              </p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='text-left p-3'>Type</th>
                    <th className='text-left p-3'>IP Address</th>
                    <th className='text-left p-3'>User Agent</th>
                    <th className='text-left p-3'>Login Time</th>
                    <th className='text-left p-3'>Last Activity</th>
                    <th className='text-left p-3'>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {userSessions.map((session) => (
                    <tr key={session.id} className='border-t'>
                      <td className='p-3'>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            session.sessionType === 'admin'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {session.sessionType}
                        </span>
                      </td>
                      <td className='p-3 font-mono text-xs'>{session.ip}</td>
                      <td
                        className='p-3 max-w-xs truncate'
                        title={session.userAgent}
                      >
                        {session.userAgent}
                      </td>
                      <td className='p-3 text-xs'>
                        {new Date(session.loginTime).toLocaleString()}
                      </td>
                      <td className='p-3 text-xs'>
                        {new Date(session.lastActivity).toLocaleString()}
                      </td>
                      <td className='p-3'>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            session.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${
                              session.isActive ? 'bg-green-500' : 'bg-gray-400'
                            }`}
                          ></div>
                          {session.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* API Metrics */}
        <div className='bg-white rounded-xl shadow-lg p-6'>
          <div className='flex items-center gap-3 mb-4'>
            <Database className='text-green-600' size={24} />
            <h3 className='font-semibold text-gray-900'>API Metrics</h3>
          </div>
          {apiMetrics.totalRequests === 0 ? (
            <div className='text-center py-8 text-gray-500'>
              <Database size={48} className='mx-auto mb-4 opacity-30' />
              <p className='text-lg font-medium'>No API Activity</p>
              <p className='text-sm'>
                No API requests have been processed yet.
              </p>
            </div>
          ) : (
            <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
              <div className='text-center'>
                <div className='text-2xl font-bold text-blue-600'>
                  {apiMetrics.totalRequests.toLocaleString()}
                </div>
                <div className='text-sm text-gray-600'>Total Requests</div>
              </div>
              <div className='text-center'>
                <div className='text-2xl font-bold text-green-600'>
                  {apiMetrics.averageResponseTime}ms
                </div>
                <div className='text-sm text-gray-600'>Avg Response Time</div>
              </div>
              <div className='text-center'>
                <div className='text-2xl font-bold text-red-600'>
                  {apiMetrics.errorRate.toFixed(2)}%
                </div>
                <div className='text-sm text-gray-600'>Error Rate</div>
              </div>
              <div className='text-center'>
                <div className='text-2xl font-bold text-purple-600'>
                  {apiMetrics.cacheHitRate.toFixed(1)}%
                </div>
                <div className='text-sm text-gray-600'>Success Rate</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminConsole;
